import { type Dispatch, type SetStateAction, useCallback } from "react";
import type { RowSelectionState } from "@tanstack/react-table";
import { pluralizeWithCount } from "@common/helpers/string";
import { useDeleteCloseMutation } from "@api/close.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { parseCloseId } from "@hooks/useOpenWorkRow";
import type { SelectedWorkEngagement } from "./useWorkRowSelection";

interface UseWorkBulkDeleteArgs {
    selectedEngagements: SelectedWorkEngagement[];
    setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
}

/**
 * Deletes the selected engagements, toasts the outcome, and unselects only
 * the rows that were deleted so failures stay selected for a retry.
 */
export const useWorkBulkDelete = ({
    selectedEngagements,
    setRowSelection,
}: UseWorkBulkDeleteArgs) => {
    const { mutateAsync: deleteClose } = useDeleteCloseMutation();
    const { toast, error } = useToaster();

    return useCallback(async () => {
        const deletions = selectedEngagements.map(({ closeId, rowIds }) => ({
            rowIds,
            closeId: parseCloseId(closeId),
        }));

        const deletableDeletions = deletions.filter(
            (deletion): deletion is { rowIds: string[]; closeId: string } =>
                deletion.closeId !== undefined,
        );
        // Rows whose closeId cannot be resolved can never be deleted, so count
        // them as failures rather than silently dropping them.
        let failedCount = deletions.length - deletableDeletions.length;

        const results = await Promise.allSettled(
            deletableDeletions.map((deletion) => deleteClose(deletion.closeId)),
        );

        let succeededCount = 0;
        const succeededRowIds = new Set<string>();
        results.forEach((result, index) => {
            if (result.status === "fulfilled") {
                succeededCount += 1;
                for (const rowId of deletableDeletions[index].rowIds) {
                    succeededRowIds.add(rowId);
                }
            } else {
                failedCount += 1;
            }
        });

        setRowSelection((prev) => {
            const next = { ...prev };
            for (const rowId of succeededRowIds) {
                delete next[rowId];
            }
            return next;
        });

        if (succeededCount > 0) {
            toast(`Deleted ${pluralizeWithCount("work item", succeededCount)}`);
        }
        if (failedCount > 0) {
            error(
                `Failed to delete ${pluralizeWithCount("work item", failedCount)}. Please try again.`,
            );
        }
    }, [selectedEngagements, setRowSelection, deleteClose, toast, error]);
};
